<template>
  <v-card elevation="0" flat tile>
    <v-card-text>
      <v-row justify="space-around">
        <v-col cols="12" md="5">
          <h2 class="primary--text font-weight-bold pt-5 pl-2">
            {{ $t('display/elevator:edit.titles.edit') }}
          </h2>

          <!-- Name -->
          <v-text-field
            outlined
            v-model="$store.state.display.elevatorForm.elevator.name"
            :label="$t('common:name')"
            :rules="[ruleRequired($t('utils:rules.required'))]"
          />

          <!-- Description -->
          <v-text-field
            outlined
            v-model="$store.state.display.elevatorForm.elevator.description"
            :label="$t('common:description')"
          />

          <!-- Address -->
          <v-text-field
            outlined
            v-model="$store.state.display.elevatorForm.elevator.address"
            :label="$t('common:address')"
            :rules="[ruleRequired($t('utils:rules.required'))]"
          />
          <v-row justify="space-around">
            <v-col cols="6" md="2">
              <!-- Address -->
              <v-text-field
                outlined
                v-model="$store.state.display.elevatorForm.elevator.zip"
                :label="$t('common:zip')"
                :rules="[ruleRequired($t('utils:rules.required'))]"
              />
            </v-col>
            <v-col cols="6" md="10">
              <!-- Address -->
              <v-text-field
                outlined
                v-model="$store.state.display.elevatorForm.elevator.city"
                :label="$t('common:city')"
                :rules="[ruleRequired($t('utils:rules.required'))]"
              />
            </v-col>
          </v-row>

          <!-- Country -->
          <v-select
            :items="regions"
            item-text="name"
            item-value="id"
            :menu-props="{bottom: true, offsetY: true}"
            v-model="$store.state.display.elevatorForm.elevator.country"
            outlined
            clearable
            @change="updateProvince($event)"
            :label="$t('common:country')"
          />

          <!-- Provinces -->
          <v-select
            :items="provincesByCountry"
            item-text="name"
            item-value="id"
            :menu-props="{bottom: true, offsetY: true}"
            v-model="$store.state.display.elevatorForm.elevator.province"
            outlined
            clearable
            :label="$t('common:province')"
          />

          <v-row justify="space-around">
            <v-col cols="6" md="6">
              <!-- Address -->
              <v-text-field
                outlined
                v-model="$store.state.display.elevatorForm.elevator.latitude"
                :label="$t('display/elevator:edit.labels.data-tab.latitude')"
                :rules="[ruleIsEmptyOrNumberText($t('utils:rules.only-number')), ruleLatitudeOrEmpty($t('utils:rules.latitude'))]"
              />
            </v-col>
            <v-col cols="6" md="6">
              <!-- Address -->
              <v-text-field
                outlined
                v-model="$store.state.display.elevatorForm.elevator.longitude"
                :label="$t('display/elevator:edit.labels.data-tab.longitude')"
                :rules="[ruleIsEmptyOrNumberText($t('utils:rules.only-number')), ruleLongitudeOrEmpty($t('utils:rules.longitude'))]"
              />
            </v-col>
          </v-row>

          <TagComponent
            :values="$store.state.display.elevatorForm.elevator.tags"
            @input="value => $store.state.display.elevatorForm.elevator.tags = value"
            :label="$t('common:tags')"
          />

          <TagComponent
            :values="$store.state.display.elevatorForm.elevator.blackTags"
            @input="value => $store.state.display.elevatorForm.elevator.blackTags = value"
            :label="$t('common:black-tags')"
          />

          <!-- Input Owner -->
          <CustomerTypeAhead
            ref="owner"
            :value="$store.state.display.elevatorForm.elevator.customer"
            @input="$store.state.display.elevatorForm.elevator.customer = $event"
            :label="$t('common:owner')"
          />

          <!-- Value -->
          <v-text-field
            outlined
            v-model="$store.state.display.elevatorForm.elevator.value"
            :label="$t('display/elevator:edit.labels.data-tab.value')"
          />

          <!-- Monthly cost -->
          <v-text-field
            outlined
            v-model="$store.state.display.elevatorForm.elevator.monthlyCost"
            :label="$t('display/elevator:edit.labels.data-tab.monthly-cost')"
            :rules="[ruleIsEmptyOrNumberText($t('utils:rules.only-number'))]"
          />

          <v-row justify="space-around">
            <v-col cols="6" md="6">
              <!-- Resolution width -->
              <v-text-field
                outlined
                v-model="$store.state.display.elevatorForm.elevator.resolutionWidth"
                :label="$t('display/elevator:edit.labels.data-tab.resolution-width')"
                :rules="[ruleIsEmptyOrInteger($t('utils:rules.onlynumerictext'))]"
              />
            </v-col>
            <v-col cols="6" md="6">
              <!-- Resolution height -->
              <v-text-field
                outlined
                v-model="$store.state.display.elevatorForm.elevator.resolutionHeight"
                :label="$t('display/elevator:edit.labels.data-tab.resolution-height')"
                :rules="[ruleIsEmptyOrInteger($t('utils:rules.onlynumerictext'))]"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="5">

          <h2 class="primary--text font-weight-bold pt-5 pl-2">
            {{ $t('display/elevator:edit.titles.data-tab.contract-period') }}
          </h2>
          <v-row justify="space-around">
            <v-col cols="6" md="6">
              <DateField
                ref="contractStart"
                @update="(v) => {$store.state.display.elevatorForm.elevator.contractStart = v; $refs.contractEnd.setMinDate(v)}"
                :isClearable="true"
                :label="$t('common:start-date')"
                :hint="$t('common:start-date')"
              />
            </v-col>
            <v-col cols="6" md="6">
              <DateField
                ref="contractEnd"
                @update="(v) => {$store.state.display.elevatorForm.elevator.contractEnd = v; $refs.contractStart.setMaxDate(v)}"
                :isClearable="true"
                :label="$t('common:end-date')"
                :hint="$t('common:end-date')"
              />
            </v-col>
          </v-row>

          <h2 class="primary--text font-weight-bold pt-5 pl-2">
            {{ $t('display/elevator:edit.titles.data-tab.sponsor-period') }}
          </h2>
          <v-row justify="space-around">
            <v-col cols="6" md="6">
              <DateField
                ref="sponsorStart"
                @update="(v) => {$store.state.display.elevatorForm.elevator.sponsorStart = v; $refs.sponsorEnd.setMinDate(v)}"
                :isClearable="true"
                :label="$t('common:start-date')"
                :hint="$t('common:start-date')"
              />
            </v-col>
            <v-col cols="6" md="6">
              <DateField
                ref="sponsorEnd"
                @update="(v) => {$store.state.display.elevatorForm.elevator.sponsorEnd = v; $refs.sponsorStart.setMaxDate(v)}"
                :isClearable="true"
                :label="$t('common:end-date')"
                :hint="$t('common:end-date')"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="8">
              <!-- Value -->
              <v-text-field
                outlined
                v-model="$store.state.display.elevatorForm.elevator.processingFee"
                @change="(v) => $store.state.display.elevatorForm.elevator.processingFee = v.replace(',','.')"
                :label="$t('display/elevator:edit.labels.data-tab.processing-fee')"
                :rules="[ruleIsEmptyOrNumberText($t('utils:rules.onlynumerictext'))]"
              />
            </v-col>
            <v-col cols="12" md="4">
              <!-- Camera -->
              <v-checkbox
                color="secondary"
                v-model="$store.state.display.elevatorForm.elevator.advertisingBonus"
                :label="$t('display/elevator:edit.labels.data-tab.advertising-bonus')"
              />
            </v-col>
          </v-row>
          <AdvertisingContract/>
          <EmergencyCenter/>


          <h2 class="primary--text font-weight-bold pt-5 pl-2">
            {{ $t('display/elevator:edit.titles.data-tab.emergency-contacts') }}
          </h2>
          <!-- Scrollview for emergency phones -->
          <v-card outlined class="mb-5" v-if="true">
            <v-virtual-scroll
              :items="$store.state.display.elevatorForm.elevator.emergencyContactList"
              :item-height="50"
              height="200"
            >
              <template v-slot="{ item }">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>{{ item }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon @click="deleteContact(item)" v-on="on">close</v-icon>
                      </template>
                      <span>{{ $t('display/elevator:edit.dialog.emergency-contact') }}</span>
                    </v-tooltip>
                  </v-list-item-action>
                </v-list-item>
                <v-divider></v-divider>
              </template>
            </v-virtual-scroll>
          </v-card>

          <!-- Dialog for adding an emergency phone -->
          <TwoButtonDialog
            ref="dialogAdd"
            :add-text-field="true"
            :add-text-field-label="$t('display/elevator:edit.dialog.emergency-contact')"
            :btn-text="$t('dialog:add-btn-text', {name : $t('display/elevator:edit.dialog.add-new-emergency-contact')})"
            color="primary"
            :dialog-title="$t('dialog:add-btn-title', {name : $t('display/elevator:edit.dialog.add-new-emergency-contact')})"
            :button1-text="$t('buttons:add')"
            :button2-text="$t('buttons:cancel')"
            @clickedButton1="addAnswer"
            @clickedButton2="$refs.dialogAdd.showDialog = false"
          />

          <!-- External emergency id -->
          <v-text-field
            class="mt-7"
            outlined
            v-model="$store.state.display.elevatorForm.elevator.externEmergencyId"
            :label="$t('display/elevator:edit.labels.data-tab.external-emergency-id')"
          />

          <!-- Mail-Msg Input -->
          <v-textarea
            outlined
            v-model="$store.state.display.elevatorForm.elevator.externEmergencyInfo"
            :label="$t('display/elevator:edit.labels.data-tab.external-emergency-info')"
            auto-grow
          />

          <!-- Channel -->
          <v-select
            :items="channel"
            item-text="name"
            item-value="id"
            :menu-props="{bottom: true, offsetY: true}"
            v-model="$store.state.display.elevatorForm.elevator.channel"
            outlined
            :label="$t('display/elevator:edit.labels.data-tab.channel')"
          />

          <!-- VHost, to select on which vhost communication between client-server occurs -->
          <v-select
              :items="vhost"
              item-text="id"
              item-value="id"
              :menu-props="{bottom: true, offsetY: true}"
              v-model="$store.state.display.elevatorForm.elevator.vhost"
              outlined
              label="VHost"
          />

          <v-row>
            <v-col cols="12" md="3">
              <!-- Active -->
              <v-checkbox
                color="secondary"
                v-model="$store.state.display.elevatorForm.elevator.active"
                :label="$t('common:active')"
              />

              <!-- Is private -->
              <v-checkbox
                color="secondary"
                v-model="$store.state.display.elevatorForm.elevator.private"
                :label="$t('display/elevator:edit.labels.data-tab.private')"
              />
            </v-col>
            <v-col cols="12" md="3">
              <!-- Is demo -->
              <v-checkbox
                color="secondary"
                v-model="$store.state.display.elevatorForm.elevator.demo"
                :label="$t('display/elevator:edit.labels.data-tab.demo')"
              />

              <!-- Is test device -->
              <v-checkbox
                color="secondary"
                v-model="$store.state.display.elevatorForm.elevator.test"
                :label="$t('display/elevator:edit.labels.data-tab.test-device')"
              />
            </v-col>
            <v-col cols="12" md="3">
              <!-- Offline mails -->
              <v-checkbox
                color="secondary"
                v-model="$store.state.display.elevatorForm.elevator.offlineMail"
                :label="$t('display/elevator:edit.labels.data-tab.offline-mail')"
              />

              <!-- Camera -->
              <v-checkbox
                color="secondary"
                v-model="$store.state.display.elevatorForm.elevator.camera"
                :label="$t('display/elevator:edit.labels.data-tab.camera')"
              />
            </v-col>
          </v-row>


        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>

import {ruleRequired, ruleIsEmptyOrNumberText, ruleIsEmptyOrInteger, ruleLongitudeOrEmpty, ruleLatitudeOrEmpty} from '@/modules/core/services/rules';
import ProvinceService from '@/modules/display/services/province-service';
import CustomerTypeAhead from '@/modules/core/components/CustomerTypeAhead';
import DateField from '@/modules/core/components/DateField';
import DateUtils from '@/modules/core/services/date-utils';
import AdvertisingContract from '@/modules/display/views/elevator/form/data-tab/AdvertisingContract';
import EmergencyCenter from '@/modules/display/views/elevator/form/data-tab/EmergencyCenter';
import TwoButtonDialog from '@/modules/core/components/TwoButtonDialog/TwoButtonDialog';
import TagComponent from '@/modules/core/components/TagComponent';

export default {
  name: "DataTab",
  components: {
    TagComponent,
    DateField,
    CustomerTypeAhead,
    AdvertisingContract,
    EmergencyCenter,
    TwoButtonDialog
  },
  data() {
    return {
      regions: [],
      provincesByCountry: [],
      channel: [],
      vhost: [
        {
          "id": "VIMA"
        },
        {
          "id": "VIEW2"
        }
      ],
      isLoadedData: false,
      isMounted: false
    }
  },
  methods: {
    ruleRequired,
    ruleIsEmptyOrNumberText,
    ruleLongitudeOrEmpty,
    ruleLatitudeOrEmpty,
    ruleIsEmptyOrInteger,
    loadRegions() {
      ProvinceService.readCountryProvince(
        (response) => {
          this.regions = response.data;
        }
      );

    },

    updateProvince(countryId) {
      for (let i = 0; i < this.regions.length; i++) {
        if (this.regions[i].id == countryId) {
          this.provincesByCountry = this.regions[i].provinces;
        }
      }
    },

    addAnswer() {
      this.$store.state.display.elevatorForm.elevator.emergencyContactList.push(this.$refs.dialogAdd.textField);
      this.$refs.dialogAdd.showDialog = false;
      this.$refs.dialogAdd.textField = "";
    },

    deleteContact(item) {
      for (var i = 0; i < this.$store.state.display.elevatorForm.elevator.emergencyContactList.length; i++) {
        if (item == this.$store.state.display.elevatorForm.elevator.emergencyContactList[i]) {
          this.$store.state.display.elevatorForm.elevator.emergencyContactList.splice(i, 1);
        }
      }
    },

    createChannel() {
      this.channel = [
        {
          "id": "release",
          "name": this.$t('display/elevator:edit.selects.release')
        },
        {
          "beta": "beta",
          "name": this.$t('display/elevator:edit.selects.beta')
        }
      ]
    },

    initialise() {
      if (this.isLoadedData && this.isMounted && this.$refs.owner) {
        this.$refs.owner.initialise(this.$store.state.display.elevatorForm.elevator.customer);

        let sponsorStart = DateUtils.parseStringToDate(this.$store.state.display.elevatorForm.elevator.sponsorStart);
        let sponsorEnd = DateUtils.parseStringToDate(this.$store.state.display.elevatorForm.elevator.sponsorEnd);

        if (sponsorStart > sponsorEnd) {
          this.$store.state.display.elevatorForm.elevator.sponsorStart
              = this.$store.state.display.elevatorForm.elevator.sponsorEnd;
        }

        if (this.$store.state.display.elevatorForm.elevator.sponsorStart != null) {
            this.$store.state.display.elevatorForm.elevator.sponsorStart
                = this.$refs.sponsorStart.parseDTO(this.$store.state.display.elevatorForm.elevator.sponsorStart);
            this.$refs.sponsorEnd.setMinDate(this.$store.state.display.elevatorForm.elevator.sponsorStart);
        }

        if (this.$store.state.display.elevatorForm.elevator.sponsorEnd != null) {
            this.$store.state.display.elevatorForm.elevator.sponsorEnd
                = this.$refs.sponsorEnd.parseDTO(this.$store.state.display.elevatorForm.elevator.sponsorEnd);
            this.$refs.sponsorStart.setMaxDate(this.$store.state.display.elevatorForm.elevator.sponsorEnd);
        }

        let contractStart = DateUtils.parseStringToDate(this.$store.state.display.elevatorForm.elevator.contractStart);
        let contractEnd = DateUtils.parseStringToDate(this.$store.state.display.elevatorForm.elevator.contractEnd);

        if (contractStart > contractEnd) {
          this.$store.state.display.elevatorForm.elevator.contractStart
              = this.$store.state.display.elevatorForm.elevator.contractEnd;
        }

        if (this.$store.state.display.elevatorForm.elevator.contractStart != null) {
            this.$store.state.display.elevatorForm.elevator.contractStart
                = this.$refs.contractStart.parseDTO(this.$store.state.display.elevatorForm.elevator.contractStart);
            this.$refs.sponsorEnd.setMinDate(this.$store.state.display.elevatorForm.elevator.contractStart);
        }

        if (this.$store.state.display.elevatorForm.elevator.contractEnd != null) {
            this.$store.state.display.elevatorForm.elevator.contractEnd
                = this.$refs.contractEnd.parseDTO(this.$store.state.display.elevatorForm.elevator.contractEnd);
            this.$refs.sponsorStart.setMaxDate(this.$store.state.display.elevatorForm.elevator.contractEnd);
        }
      }
    },

    loadedData() {
      this.isLoadedData = true;
      this.initialise();
      if (this.$store.state.display.elevatorForm.elevator.country != null) {
          this.updateProvince(this.$store.state.display.elevatorForm.elevator.country);
      }
    },

    resetData() {
      if (this.$refs.sponsorStart) {
        this.$refs.sponsorStart.resetData();
      }
      if (this.$refs.sponsorEnd) {
        this.$refs.sponsorEnd.resetData();
      }
      if (this.$refs.contractStart) {
        this.$refs.contractStart.resetData();
      }
      if (this.$refs.contractEnd) {
        this.$refs.contractEnd.resetData();
      }
    }
  },
  mounted() {
    this.isMounted = true;
    this.initialise();
  },
  created() {
    this.loadRegions();
    this.createChannel();
    this.$store.commit('display/elevatorForm/addListener', this.loadedData);
    this.$store.commit('display/elevatorForm/addCloseListener', this.resetData);
  }
}
</script>

<style scoped>

</style>
